import GlobalStyles from '@mui/material/GlobalStyles'
import React from 'react'
import { useSelector } from 'react-redux'

import { selectToken } from '@/app/module/auth/store/selectors'
import { useGetUser } from '@/app/module/user/hooks/use-get-user'
import { formatName } from '@/app/service/util/format'
import config from '@/config'

import { type ClearFeedUser, initializeLoadedClearFeed, loadClearFeed, resetClearFeedSession } from './setup'

type UserDetails = {
  clearFeed?: string
  firstName?: string
  lastName?: string
  username?: string
}

export const getClearFeedUser = (details?: UserDetails): ClearFeedUser | undefined => {
  if (!details) {
    return undefined
  }

  const email = details.username?.trim()
  const fullName = formatName(details.firstName, details.lastName)
  const hash = details.clearFeed?.trim()

  if (!email || !fullName || !hash) {
    return undefined
  }

  return {
    email,
    hash,
    name: fullName,
  }
}

const { clientId } = config.clearFeed

const ClearFeedIntegration: React.FC = () => {
  const token = useSelector(selectToken)
  const { data: details, isFetching: userLoading } = useGetUser({ skip: !clientId || !token })
  const user = React.useMemo(() => getClearFeedUser(details), [details])
  const clearFeedUser = token ? user : undefined
  const wasAuthenticated = React.useRef(Boolean(token))

  React.useEffect(() => {
    const didLogOut = wasAuthenticated.current && !token
    wasAuthenticated.current = Boolean(token)

    if (didLogOut) {
      if (clientId) {
        resetClearFeedSession()
      }
      return
    }

    if (!clientId || (token && (userLoading || !clearFeedUser))) {
      return
    }

    let active = true

    const initialize = async () => {
      const clearFeed = await loadClearFeed()

      if (active) {
        await initializeLoadedClearFeed(clearFeed, clientId, clearFeedUser)
      }
    }

    initialize().catch(() => undefined)

    return () => {
      active = false
    }
  }, [clearFeedUser, token, userLoading])

  return <GlobalStyles styles={clearFeedGlobalStyles} />
}

export const clearFeedGlobalStyles = {
  '@media (max-width: 639px)': {
    '#clearfeed-host': {
      position: 'relative',
      zIndex: 1202,
    },
  },
}

export default ClearFeedIntegration
